import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import Dialog from "@mui/material/Dialog";
import {
  Grid,
  Button,
  TextField,
  Stack,
  TextareaAutosize,
  Typography,
  Avatar,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@common-ui/ui";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { addContact, updateCustomerContact } from "../../../services";

const ContactDialog = ({
  open,
  mode,
  currentId,
  customerDetails,
  setIsSuccess,
  handleClose,
  setToastOpen,
}) => {
  const [clientAvatarInitials, setClientAvatarInitials] = useState(null);

  const formik = useFormik({
    initialValues: {
      // Contact Info
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      title: null,
      description: "",
      notes: "",
    },
    validationSchema: Yup.object({
      // Contact Info
      firstName: Yup.string().trim().required("First Name is required"),
      lastName: Yup.string().trim().required("Last Name is required"),
      email: Yup.string().nullable().email().trim(),
      phone: Yup.number(),
      title: Yup.string().trim().nullable(),
      description: Yup.string().nullable().trim(),
      notes: Yup.string().nullable().trim(),
    }),
    onSubmit: () => {
      async function submit() {
        let { firstName, lastName, email, phone, title, description, notes } =
          formik.values;
        const attributes = {
          first_name: firstName,
          last_name: lastName,
          title:
            title === null ? "" : String(title) === "None" ? "" : String(title),
          description: description === null ? "" : description,
          phone,
          email,
          notes: notes === null ? "" : notes,
        };
        if (mode === "edit") {
          try {
            await updateCustomerContact(
              customerDetails.org,
              customerDetails.system_id,
              currentId,
              attributes
            );
            formik.resetForm();
            setToastOpen(true);
            setIsSuccess(true);
            handleClose();
          } catch (error) {
            alert(error);
          }
        } else {
          await addContact(
            attributes,
            customerDetails.org,
            customerDetails.system_id
          );
          formik.resetForm();
          setToastOpen(true);
          setIsSuccess(true);
          handleClose();
        }
      }
      submit();
    },
  });

  useEffect(() => {
    if (mode === "edit") {
      let [currentContact] = customerDetails?.contacts?.filter(
        (contact) => contact.system_id === currentId
      );
      formik.setFieldValue("firstName", currentContact?.first_name);
      formik.setFieldValue("lastName", currentContact?.last_name);
      formik.setFieldValue("email", currentContact?.email);
      formik.setFieldValue("title", currentContact?.title);
      formik.setFieldValue("phone", currentContact?.phone);
      formik.setFieldValue("description", currentContact?.description);
      formik.setFieldValue("notes", currentContact?.notes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMemo(() => {
    if (currentId) {
      let [currentContact] = customerDetails.contacts.filter(
        (contact) => contact.system_id === currentId
      );
      formik.setFieldValue("firstName", currentContact?.first_name);
      formik.setFieldValue("lastName", currentContact?.last_name);
      formik.setFieldValue("email", currentContact?.email);
      formik.setFieldValue("title", currentContact?.title);
      formik.setFieldValue("phone", currentContact?.phone);
      formik.setFieldValue("description", currentContact?.description);
      formik.setFieldValue("notes", currentContact?.notes);
    } else {
      formik.setFieldValue("firstName", "");
      formik.setFieldValue("lastName", "");
      formik.setFieldValue("email", "");
      formik.setFieldValue("title", null);
      formik.setFieldValue("phone", "");
      formik.setFieldValue("description", "");
      formik.setFieldValue("notes", "");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  useEffect(() => {
    const fullName = formik.values.firstName + " " + formik.values.lastName;
    setClientAvatarInitials(fullName);
  }, [formik.values.firstName, formik.values.lastName]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        formik.handleReset();
        handleClose();
        setClientAvatarInitials(null);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableEscapeKeyDown={true}
      maxWidth="xlg"
      scroll="paper"
    >
      <DialogTitle id="alert-dialog-title">
        {mode === "edit" ? "Edit" : "Create"} New Contact
        {open ? (
          <IconButton
            aria-label="close"
            onClick={() => {
              formik.handleReset();
              handleClose();
              setClientAvatarInitials(null);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers={true}>
          <Grid direction="row" container spacing={1}>
            <Grid container item sm={2} alignItems="center" spacing={1}>
              {/* Contact Info */}
              <Grid item md={12} xs={12}>
                <div
                  style={{
                    display: "flex",
                    padding: 1.5,
                    margin: 0.5,
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "50%",
                  }}
                >
                  <Avatar
                    fontSize="large"
                    sx={{
                      height: "5em",
                      width: "5em",
                      backgroundColor: "grey",
                    }}
                    src={
                      formik.values.firstName !== "" ||
                      formik.values.lastName !== ""
                        ? `https://avatars.dicebear.com/v2/initials/${clientAvatarInitials}.svg?chars=2`
                        : ""
                    }
                  />
                </div>
              </Grid>
            </Grid>
            <Grid
              container
              item
              sm={10}
              spacing={1}
              columns={{ xs: 12, md: 12 }}
            >
              <Grid item md={4} xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} variant="h4" fontWeight="700">
                    First Name *
                  </Typography>
                </Stack>
                <TextField
                  sx={{ mt: "8px" }}
                  size="small"
                  autoComplete="off"
                  error={Boolean(
                    formik.touched.firstName && formik.errors.firstName
                  )}
                  fullWidth
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  placeholder="Type"
                  margin="normal"
                  name="firstName"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="text"
                  value={formik.values.firstName}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} variant="h4" fontWeight="700">
                    Last Name *
                  </Typography>
                </Stack>
                <TextField
                  sx={{ mt: "8px" }}
                  size="small"
                  autoComplete="off"
                  error={Boolean(
                    formik.touched.lastName && formik.errors.lastName
                  )}
                  fullWidth
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  placeholder="Type"
                  margin="normal"
                  name="lastName"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="text"
                  value={formik.values.lastName}
                />
              </Grid>
              <Grid item md={3} xs={12}></Grid>
              <Grid item md={4} xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} variant="h4" fontWeight="700">
                    Email
                  </Typography>
                </Stack>
                <TextField
                  sx={{ mt: "8px" }}
                  size="small"
                  autoComplete="off"
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  fullWidth
                  helperText={formik.touched.email && formik.errors.email}
                  placeholder="Type"
                  margin="normal"
                  name="email"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="email"
                  value={formik.values.email}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} variant="h4" fontWeight="700">
                    Phone
                  </Typography>
                </Stack>
                <Stack direction="row">
                  <TextField
                    inputProps={{
                      inputMode: "numeric",
                    }}
                    sx={{ mt: "8px" }}
                    size="small"
                    autoComplete="off"
                    error={Boolean(formik.touched.phone && formik.errors.phone)}
                    fullWidth
                    helperText={formik.touched.phone && formik.errors.phone}
                    placeholder="Type"
                    margin="normal"
                    name="phone"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="string"
                    value={formik.values.phone}
                  />
                </Stack>
              </Grid>
              <Grid item md={4} xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} variant="h4" fontWeight="700">
                    Title
                  </Typography>
                </Stack>
                <TextField
                  sx={{ mt: "8px" }}
                  size="small"
                  autoComplete="off"
                  error={Boolean(formik.touched.title && formik.errors.title)}
                  fullWidth
                  helperText={formik.touched.title && formik.errors.title}
                  placeholder="Type"
                  margin="normal"
                  name="title"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.title}
                />
              </Grid>
              <Grid item md={8} xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} variant="h4" fontWeight="700">
                    Comments
                  </Typography>
                </Stack>
                <TextField
                  sx={{ mt: 1 }}
                  size="small"
                  InputProps={{
                    inputComponent: TextareaAutosize,
                    inputProps: { minRows: 4 },
                  }}
                  autoComplete="off"
                  error={Boolean(formik.touched.notes && formik.errors.notes)}
                  fullWidth
                  helperText={formik.touched.notes && formik.errors.notes}
                  placeholder="Type"
                  margin="normal"
                  name="notes"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="notes"
                  value={formik.values.notes}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            size="large"
            variant="text"
            onClick={() => {
              formik.handleReset();
              handleClose();
              setClientAvatarInitials(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={formik.isSubmitting || !(formik.isValid && formik.dirty)}
            size="large"
            type="submit"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactDialog;
